import { useDashboardCustomer } from '@/contexts/DashboardCustomerContext';
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  X,
  ExternalLink,
  User,
  Clock,
  Tag,
  MessageSquare,
  Building,
  Users,
  AlertCircle,
  CheckCircle,
  AlertTriangle,
  Info,
  Paperclip,
  CheckSquare,
  Timer,
  MoreHorizontal,
  ChevronDown,
} from 'lucide-react';
import { helpdeskApi, TicketDetail, TicketComment, TicketAttachment, TicketTask, TicketHistory, TicketTimeEntry } from '@/lib/api/helpdesk';
import { createPortal } from 'react-dom';

interface TicketDetailModalProps {
  ticketId: string;
  onClose: () => void;
}

type TabType = 'info' | 'conversation' | 'resolution' | 'time_entry' | 'attachment' | 'task' | 'history';

const TicketDetailModal: React.FC<TicketDetailModalProps> = ({ ticketId, onClose }) => {
  const { customer_id } = useDashboardCustomer();
  const [ticket, setTicket] = useState<TicketDetail | null>(null);
  const [fullDetails, setFullDetails] = useState<any>(null);
  const [comments, setComments] = useState<TicketComment[]>([]);
  const [attachments, setAttachments] = useState<TicketAttachment[]>([]);
  const [tasks, setTasks] = useState<TicketTask[]>([]);
  const [history, setHistory] = useState<TicketHistory[]>([]);
  const [timeEntries, setTimeEntries] = useState<TicketTimeEntry[]>([]);
  const [resolution, setResolution] = useState<any>(null);
  const [contactProfiles, setContactProfiles] = useState<any[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingComments, setLoadingComments] = useState(false);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingTimeEntries, setLoadingTimeEntries] = useState(false);
  const [loadingResolution, setLoadingResolution] = useState(false);
  const [loadingContactProfiles, setLoadingContactProfiles] = useState(false);
  const [loadingFullDetails, setLoadingFullDetails] = useState(false);
  const [assigneePhotoError, setAssigneePhotoError] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('info');
  const [historyFilter, setHistoryFilter] = useState({
    eventType: 'all',
    actor: 'all',
  });

  // Helper to generate initials from name
  const getInitials = (firstName?: string, lastName?: string) => {
    const first = firstName?.charAt(0) || '';
    const last = lastName?.charAt(0) || '';
    return (first + last).toUpperCase() || '?';
  };

  // Helper to extract agent ID from Zoho photo URL
  const extractAgentId = (photoURL: string) => {
    const match = photoURL.match(/\/agents\/(\d+)\/photo/);
    return match ? match[1] : null;
  };

  // Helper to build proxied photo URL
  const getProxiedPhotoUrl = (photoURL: string, configId?: string) => {
    if (!photoURL || !configId) return null;
    const agentId = extractAgentId(photoURL);
    if (!agentId) return null;
    // Pass the original photoURL so backend can extract orgId from it
    return `/api/v1/helpdesk/agents/${agentId}/photo?config_id=${configId}&customer_id=${encodeURIComponent(customer_id)}&ticket_id=${encodeURIComponent(ticketId)}&photo_url=${encodeURIComponent(photoURL)}`;
  };

  // Disable body scroll when modal is open
  useEffect(() => {
    // Set overflow hidden on both html and body elements
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    return () => {
      document.documentElement.style.overflow = 'unset';
      document.body.style.overflow = 'unset';
    };
  }, []);

  useEffect(() => {
    // Reset photo error state when ticket changes
    setAssigneePhotoError(false);
    fetchTicket();
    fetchFullDetails();
  }, [ticketId, customer_id]);

  const fetchTicket = async () => {
    try {
      setLoading(true);
      const data = await helpdeskApi.getTicket(ticketId, customer_id);
      setTicket(data);
    } catch (error) {
      console.error('Failed to fetch ticket:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFullDetails = async () => {
    try {
      setLoadingFullDetails(true);
      const data = await helpdeskApi.getTicketFullDetails(ticketId, customer_id);
      setFullDetails(data);
    } catch (error) {
      console.error('Failed to fetch full ticket details:', error);
    } finally {
      setLoadingFullDetails(false);
    }
  };

  const fetchComments = async () => {
    if (comments.length > 0) return;

    try {
      setLoadingComments(true);
      const data = await helpdeskApi.getTicketComments(ticketId, customer_id);
      setComments(data);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    } finally {
      setLoadingComments(false);
    }
  };

  const fetchAttachments = async () => {
    if (attachments.length > 0) return;

    try {
      setLoadingAttachments(true);
      const data = await helpdeskApi.getTicketAttachments(ticketId, customer_id);
      setAttachments(data);
    } catch (error) {
      console.error('Failed to fetch attachments:', error);
    } finally {
      setLoadingAttachments(false);
    }
  };

  const fetchTasks = async () => {
    if (tasks.length > 0) return;

    try {
      setLoadingTasks(true);
      const data = await helpdeskApi.getTicketTasks(ticketId, customer_id);
      setTasks(data);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    } finally {
      setLoadingTasks(false);
    }
  };

  const fetchHistory = async () => {
    if (history.length > 0) return;

    try {
      setLoadingHistory(true);
      const data = await helpdeskApi.getTicketHistory(ticketId, customer_id);
      setHistory(data);
    } catch (error) {
      console.error('Failed to fetch history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const fetchTimeEntries = async () => {
    if (timeEntries.length > 0) return;

    try {
      setLoadingTimeEntries(true);
      const data = await helpdeskApi.getTicketTimeEntries(ticketId, customer_id);
      setTimeEntries(data);
    } catch (error) {
      console.error('Failed to fetch time entries:', error);
    } finally {
      setLoadingTimeEntries(false);
    }
  };

  const fetchResolution = async () => {
    if (resolution !== null) return;

    try {
      setLoadingResolution(true);
      const data = await helpdeskApi.getTicketResolution(ticketId, customer_id);
      setResolution(data);
    } catch (error) {
      console.error('Failed to fetch resolution:', error);
      setResolution({});
    } finally {
      setLoadingResolution(false);
    }
  };

  const fetchContactProfiles = async () => {
    if (contactProfiles !== null) return;

    try {
      setLoadingContactProfiles(true);
      const data = await helpdeskApi.getTicketContactProfiles(ticketId, customer_id);
      setContactProfiles(data);
    } catch (error) {
      console.error('Failed to fetch contact profiles:', error);
      // Set empty array on error - will fall back to DB contact info
      setContactProfiles([]);
    } finally {
      setLoadingContactProfiles(false);
    }
  };

  // Fetch data when switching tabs
  useEffect(() => {
    if (!ticket) return;

    if (activeTab === 'info' && contactProfiles === null) {
      fetchContactProfiles();
    } else if (activeTab === 'conversation' && comments.length === 0) {
      fetchComments();
    } else if (activeTab === 'resolution' && resolution === null) {
      fetchResolution();
    } else if (activeTab === 'time_entry' && timeEntries.length === 0) {
      fetchTimeEntries();
    } else if (activeTab === 'attachment' && attachments.length === 0) {
      fetchAttachments();
    } else if (activeTab === 'task' && tasks.length === 0) {
      fetchTasks();
    } else if (activeTab === 'history' && history.length === 0) {
      fetchHistory();
    }
  }, [activeTab, ticket]);

  const getPriorityIcon = (priority?: string) => {
    switch (priority?.toLowerCase()) {
      case 'urgent':
        return <AlertCircle className="w-5 h-5" />;
      case 'high':
        return <AlertTriangle className="w-5 h-5" />;
      case 'medium':
        return <Info className="w-5 h-5" />;
      case 'low':
        return <CheckCircle className="w-5 h-5" />;
      default:
        return <Info className="w-5 h-5" />;
    }
  };

  const getPriorityColor = (priority?: string) => {
    switch (priority?.toLowerCase()) {
      case 'urgent':
        return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border-red-200 dark:border-red-800';
      case 'high':
        return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 border-orange-200 dark:border-orange-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border-green-200 dark:border-green-800';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400 border-gray-200 dark:border-gray-600';
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status?.toLowerCase()) {
      case 'open':
      case 'in progress':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
      case 'resolved':
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      case 'closed':
        return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400';
      case 'on hold':
        return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }) + ' ' + date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-end"
      onClick={onClose}
    >
      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="bg-white dark:bg-slate-800 h-full w-full max-w-4xl shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">Loading ticket details...</p>
            </div>
          </div>
        ) : ticket ? (
          <>
            {/* Ticket Header with Blue Gradient */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-slate-900 dark:to-slate-800 border-b-2 border-blue-200 dark:border-blue-800 px-6 py-4">
              <div className="flex items-center justify-between mb-3">
                {/* Left side - Ticket Number and Title */}
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-12 h-12 rounded-full bg-white dark:bg-slate-800 border-2 border-gray-300 dark:border-gray-600 flex items-center justify-center flex-shrink-0">
                    <MessageSquare className="w-6 h-6 text-gray-600 dark:text-gray-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white truncate">
                      #{ticket.ticket_number} {ticket.subject || 'No subject'}
                    </h2>
                  </div>
                </div>

                {/* Right side - Close button */}
                <button
                  onClick={onClose}
                  className="p-2 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors flex-shrink-0"
                >
                  <X className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                </button>
              </div>

              {/* Metadata Row */}
              <div className="flex items-center gap-4 text-sm flex-wrap">
                {/* Created Date */}
                <div className="flex items-center gap-1.5 text-gray-700 dark:text-gray-300">
                  <Clock className="w-4 h-4" />
                  <span>{formatDate(ticket.created_time)}</span>
                </div>

                {/* Tags */}
                {ticket.tags && ticket.tags.length > 0 && (
                  <div className="flex items-center gap-1.5 text-gray-700 dark:text-gray-300">
                    <Tag className="w-4 h-4" />
                    <span>Tags ({ticket.tags.length})</span>
                  </div>
                )}

                {/* Status Badge */}
                <span className={`px-3 py-1 rounded-md text-xs font-medium ${getStatusColor(ticket.status)}`}>
                  {ticket.status}
                </span>

                {/* Priority Badge */}
                <div className={`flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-medium border ${getPriorityColor(ticket.priority)}`}>
                  {getPriorityIcon(ticket.priority)}
                  <span>{ticket.priority}</span>
                </div>

                {/* External Link */}
                {ticket.web_url && (
                  <a
                    href={ticket.web_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-blue-600 dark:text-blue-400 hover:underline"
                    title="Open in helpdesk"
                  >
                    <ExternalLink className="w-4 h-4" />
                    <span>View in Zoho</span>
                  </a>
                )}
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="bg-blue-50/50 dark:bg-slate-900/50 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center px-6">
                {/* Info Tab */}
                <TabButton
                  label="INFO"
                  isActive={activeTab === 'info'}
                  onClick={() => setActiveTab('info')}
                />

                {/* Conversation Tab */}
                <TabButton
                  label={`${ticket.comment_count || 0} CONVERSATION`}
                  isActive={activeTab === 'conversation'}
                  onClick={() => setActiveTab('conversation')}
                  hasDropdown={true}
                />

                {/* Resolution Tab */}
                <TabButton
                  label="RESOLUTION"
                  isActive={activeTab === 'resolution'}
                  onClick={() => setActiveTab('resolution')}
                />

                {/* Time Entry Tab */}
                <TabButton
                  label={timeEntries.length > 0 ? `${timeEntries.length} TIME ENTRY` : 'TIME ENTRY'}
                  isActive={activeTab === 'time_entry'}
                  onClick={() => setActiveTab('time_entry')}
                />

                {/* Attachment Tab */}
                <TabButton
                  label="ATTACHMENT"
                  isActive={activeTab === 'attachment'}
                  onClick={() => setActiveTab('attachment')}
                />

                {/* Task Tab */}
                <TabButton
                  label="TASK"
                  isActive={activeTab === 'task'}
                  onClick={() => setActiveTab('task')}
                />

                {/* History Tab */}
                <TabButton
                  label="HISTORY"
                  isActive={activeTab === 'history'}
                  onClick={() => setActiveTab('history')}
                />

                {/* More menu */}
                <div className="ml-auto">
                  <button className="p-3 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors rounded">
                    <MoreHorizontal className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                  </button>
                </div>
              </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto">
              {/* Info Tab */}
              {activeTab === 'info' && (
                <div className="p-6 space-y-6">
                  {/* Ticket Info Cards */}
                  <div className="grid grid-cols-2 gap-4">
                    {(ticket.contact_email || ticket.contact_name) && (
                      <InfoCard
                        icon={<User className="w-5 h-5" />}
                        label="Contact"
                        value={ticket.contact_name || ticket.contact_email || 'Unknown'}
                        subtitle={ticket.contact_name && ticket.contact_email ? ticket.contact_email : undefined}
                      />
                    )}
                    {ticket.assignee_name && (
                      <InfoCard
                        icon={<Users className="w-5 h-5" />}
                        label="Assigned To"
                        value={ticket.assignee_name}
                        subtitle={ticket.assignee_email}
                      />
                    )}
                    {ticket.account_name && (
                      <InfoCard
                        icon={<Building className="w-5 h-5" />}
                        label="Account"
                        value={ticket.account_name}
                      />
                    )}
                    {(ticket.department_name || ticket.team_name) && (
                      <InfoCard
                        icon={<Building className="w-5 h-5" />}
                        label="Department"
                        value={ticket.department_name || ticket.team_name || ''}
                        subtitle={ticket.department_name && ticket.team_name ? ticket.team_name : undefined}
                      />
                    )}
                    {ticket.channel && (
                      <InfoCard
                        icon={<Building className="w-5 h-5" />}
                        label="Channel"
                        value={ticket.channel}
                      />
                    )}
                    {ticket.category && (
                      <InfoCard
                        icon={<Tag className="w-5 h-5" />}
                        label="Category"
                        value={ticket.category}
                        subtitle={ticket.sub_category}
                      />
                    )}
                  </div>

                  {/* Enhanced Contact Details from Full API */}
                  {loadingFullDetails ? (
                    <ContactDetailsSkeleton />
                  ) : fullDetails?.contact ? (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2 uppercase tracking-wide">
                        <User className="w-4 h-4" />
                        Full Contact Details
                      </h3>
                      <div className="bg-gray-50 dark:bg-slate-900/50 rounded-xl p-4 space-y-2">
                        {(fullDetails.contact.firstName || fullDetails.contact.lastName) && (
                          <div className="flex items-start gap-2">
                            <span className="text-sm text-gray-500 dark:text-gray-400 w-24">Name:</span>
                            <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                              {`${fullDetails.contact.firstName || ''} ${fullDetails.contact.lastName || ''}`.trim()}
                            </span>
                          </div>
                        )}
                        {fullDetails.contact.email && (
                          <div className="flex items-start gap-2">
                            <span className="text-sm text-gray-500 dark:text-gray-400 w-24">Email:</span>
                            <a href={`mailto:${fullDetails.contact.email}`} className="text-sm text-blue-600 dark:text-blue-400 hover:underline">
                              {fullDetails.contact.email}
                            </a>
                          </div>
                        )}
                        {fullDetails.contact.phone && (
                          <div className="flex items-start gap-2">
                            <span className="text-sm text-gray-500 dark:text-gray-400 w-24">Phone:</span>
                            <span className="text-sm text-gray-700 dark:text-gray-300">{fullDetails.contact.phone}</span>
                          </div>
                        )}
                        {fullDetails.contact.mobile && (
                          <div className="flex items-start gap-2">
                            <span className="text-sm text-gray-500 dark:text-gray-400 w-24">Mobile:</span>
                            <span className="text-sm text-gray-700 dark:text-gray-300">{fullDetails.contact.mobile}</span>
                          </div>
                        )}
                        {fullDetails.contact.accountName && (
                          <div className="flex items-start gap-2">
                            <span className="text-sm text-gray-500 dark:text-gray-400 w-24">Account:</span>
                            <span className="text-sm text-gray-700 dark:text-gray-300">{fullDetails.contact.accountName}</span>
                          </div>
                        )}

                        {/* Contact Profiles - Lazy loaded from API */}
                        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                          <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2 uppercase tracking-wide">
                            Social Profiles
                          </h4>
                          {loadingContactProfiles ? (
                            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                              <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                              <span>Loading social profiles...</span>
                            </div>
                          ) : contactProfiles && contactProfiles.length > 0 ? (
                            <div className="space-y-2">
                              {contactProfiles.map((profile: any, index: number) => (
                                <div key={index} className="flex items-start gap-2">
                                  <span className="text-sm text-gray-500 dark:text-gray-400 w-24 capitalize">
                                    {profile.type || profile.name}:
                                  </span>
                                  {profile.url ? (
                                    <a
                                      href={profile.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                                    >
                                      {profile.value || profile.url}
                                    </a>
                                  ) : (
                                    <span className="text-sm text-gray-700 dark:text-gray-300">
                                      {profile.value || profile.username || 'N/A'}
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500 dark:text-gray-400">No social profiles available</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : null}

                  {/* Enhanced Assignee Details from Full API */}
                  {loadingFullDetails ? (
                    <ContactDetailsSkeleton />
                  ) : fullDetails?.assignee ? (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2 uppercase tracking-wide">
                        <Users className="w-4 h-4" />
                        Assignee Details
                      </h3>
                      <div className="bg-gray-50 dark:bg-slate-900/50 rounded-xl p-4">
                        <div className="flex items-start gap-4">
                          {/* Assignee Photo with Proxy and Fallback */}
                          {fullDetails.assignee.photoURL && !assigneePhotoError && getProxiedPhotoUrl(fullDetails.assignee.photoURL, fullDetails.config_id) ? (
                            <img
                              src={getProxiedPhotoUrl(fullDetails.assignee.photoURL, fullDetails.config_id)!}
                              alt={`${fullDetails.assignee.firstName} ${fullDetails.assignee.lastName}`}
                              className="w-16 h-16 rounded-full object-cover border-2 border-gray-200 dark:border-gray-700"
                              onError={() => {
                                console.log('Agent photo failed to load, showing initials fallback');
                                setAssigneePhotoError(true);
                              }}
                            />
                          ) : (
                            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-semibold text-xl border-2 border-gray-200 dark:border-gray-700">
                              {getInitials(fullDetails.assignee.firstName, fullDetails.assignee.lastName)}
                            </div>
                          )}
                          <div className="flex-1 space-y-2">
                            {(fullDetails.assignee.firstName || fullDetails.assignee.lastName) && (
                              <div className="flex items-start gap-2">
                                <span className="text-sm text-gray-500 dark:text-gray-400 w-24">Name:</span>
                                <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                                  {`${fullDetails.assignee.firstName || ''} ${fullDetails.assignee.lastName || ''}`.trim()}
                                </span>
                              </div>
                            )}
                            {fullDetails.assignee.email && (
                              <div className="flex items-start gap-2">
                                <span className="text-sm text-gray-500 dark:text-gray-400 w-24">Email:</span>
                                <a href={`mailto:${fullDetails.assignee.email}`} className="text-sm text-blue-600 dark:text-blue-400 hover:underline">
                                  {fullDetails.assignee.email}
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : null}

                  {/* Tags */}
                  {ticket.tags && ticket.tags.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">
                        Tags
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {ticket.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full text-sm"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Conversation Tab */}
              {activeTab === 'conversation' && (
                <div className="p-6 space-y-6">
                  {/* Description */}
                  {(fullDetails?.description || ticket.description) && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">
                        Description
                      </h3>
                      <div className="bg-gray-50 dark:bg-slate-900/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                        <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                          {fullDetails?.description || ticket.description}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Comments/Conversation */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wide">
                      Comments ({comments.length})
                    </h3>
                    {loadingComments ? (
                      <div className="text-center py-8">
                        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                        <p className="text-gray-600 dark:text-gray-400">Loading comments...</p>
                      </div>
                    ) : comments.length === 0 ? (
                      <div className="bg-gray-50 dark:bg-slate-900/50 rounded-lg p-8 text-center border border-gray-200 dark:border-gray-700">
                        <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-500 dark:text-gray-400">No comments yet</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {comments.map((comment) => (
                          <CommentCard key={comment.id} comment={comment} />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Resolution Tab */}
              {activeTab === 'resolution' && (
                <div className="p-6 space-y-6">
                  {loadingResolution ? (
                    <div className="bg-gray-50 dark:bg-slate-900/50 rounded-lg p-8 text-center border border-gray-200 dark:border-gray-700">
                      <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                      <p className="text-gray-600 dark:text-gray-400">Loading resolution...</p>
                    </div>
                  ) : resolution && (resolution.content || resolution.plainText) ? (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">
                        Resolution
                      </h3>
                      <div className="bg-green-50 dark:bg-green-900/10 rounded-lg p-4 border border-green-200 dark:border-green-800">
                        <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                          {resolution.plainText || resolution.content}
                        </p>
                      </div>
                      {resolution.attachmentCount > 0 && (
                        <div className="mt-4">
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            📎 {resolution.attachmentCount} attachment{resolution.attachmentCount > 1 ? 's' : ''}
                          </p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="bg-gray-50 dark:bg-slate-900/50 rounded-lg p-8 text-center border border-gray-200 dark:border-gray-700">
                      <CheckCircle className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-500 dark:text-gray-400">No resolution provided yet</p>
                    </div>
                  )}
                </div>
              )}

              {/* Time Entry Tab */}
              {activeTab === 'time_entry' && (
                <div className="p-6">
                  {loadingTimeEntries ? (
                    <div className="text-center py-8">
                      <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                      <p className="text-gray-600 dark:text-gray-400">Loading time entries...</p>
                    </div>
                  ) : timeEntries.length === 0 ? (
                    <div className="bg-gray-50 dark:bg-slate-900/50 rounded-lg p-8 text-center border border-gray-200 dark:border-gray-700">
                      <Timer className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-500 dark:text-gray-400">No time entries recorded</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {timeEntries.map((entry) => (
                        <TimeEntryCard key={entry.id} entry={entry} />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Attachment Tab */}
              {activeTab === 'attachment' && (
                <div className="p-6">
                  {loadingAttachments ? (
                    <div className="text-center py-8">
                      <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                      <p className="text-gray-600 dark:text-gray-400">Loading attachments...</p>
                    </div>
                  ) : attachments.length === 0 ? (
                    <div className="bg-gray-50 dark:bg-slate-900/50 rounded-lg p-8 text-center border border-gray-200 dark:border-gray-700">
                      <Paperclip className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-500 dark:text-gray-400">No attachments found</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {attachments.map((attachment) => (
                        <AttachmentCard key={attachment.id} attachment={attachment} />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Task Tab */}
              {activeTab === 'task' && (
                <div className="p-6">
                  {loadingTasks ? (
                    <div className="text-center py-8">
                      <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                      <p className="text-gray-600 dark:text-gray-400">Loading tasks...</p>
                    </div>
                  ) : tasks.length === 0 ? (
                    <div className="bg-gray-50 dark:bg-slate-900/50 rounded-lg p-8 text-center border border-gray-200 dark:border-gray-700">
                      <CheckSquare className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-500 dark:text-gray-400">No tasks found</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {tasks.map((task) => (
                        <TaskCard key={task.id} task={task} />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* History Tab */}
              {activeTab === 'history' && (
                <div className="p-6 space-y-4">
                  {/* Filters */}
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-slate-900 dark:to-slate-800 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                    <div className="flex flex-wrap gap-4">
                      {/* Event Type Filter */}
                      <div className="flex-1 min-w-[200px]">
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">
                          Event Type
                        </label>
                        <select
                          value={historyFilter.eventType}
                          onChange={(e) => setHistoryFilter({ ...historyFilter, eventType: e.target.value })}
                          className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="all">All Events</option>
                          <option value="Ticket_Updated">Ticket Updated</option>
                          <option value="NotificationRule_Applied">Notifications</option>
                          <option value="CustomFunction_Triggered">Custom Functions</option>
                          <option value="Ticket_Created">Ticket Created</option>
                          <option value="Comment_Added">Comments</option>
                          <option value="Attachment_Added">Attachments</option>
                        </select>
                      </div>

                      {/* Actor Filter */}
                      <div className="flex-1 min-w-[200px]">
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">
                          Actor
                        </label>
                        <select
                          value={historyFilter.actor}
                          onChange={(e) => setHistoryFilter({ ...historyFilter, actor: e.target.value })}
                          className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="all">All Actors</option>
                          <option value="System">System</option>
                          {Array.from(new Set(history.map(h => h.actor).filter(Boolean)))
                            .filter(actor => actor !== 'System')
                            .map(actor => (
                              <option key={actor} value={actor}>{actor}</option>
                            ))
                          }
                        </select>
                      </div>

                      {/* Clear Filters Button */}
                      <div className="flex items-end">
                        <button
                          onClick={() => setHistoryFilter({ eventType: 'all', actor: 'all' })}
                          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                        >
                          Clear Filters
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* History Events */}
                  {loadingHistory ? (
                    <div className="bg-gray-50 dark:bg-slate-900/50 rounded-lg p-8 text-center border border-gray-200 dark:border-gray-700">
                      <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                      <p className="text-sm text-gray-500 dark:text-gray-400">Loading history...</p>
                    </div>
                  ) : history.length === 0 ? (
                    <div className="bg-gray-50 dark:bg-slate-900/50 rounded-lg p-8 text-center border border-gray-200 dark:border-gray-700">
                      <Clock className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-500 dark:text-gray-400">No history events found</p>
                    </div>
                  ) : (
                    <div className="bg-gray-50 dark:bg-slate-900/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700 space-y-3">
                      {history
                        .filter(event => {
                          // Apply event type filter
                          if (historyFilter.eventType !== 'all' && event.action !== historyFilter.eventType) {
                            return false;
                          }
                          // Apply actor filter
                          if (historyFilter.actor !== 'all' && event.actor !== historyFilter.actor) {
                            return false;
                          }
                          return true;
                        })
                        .map((event, index) => (
                          <HistoryEventCard key={event.id || index} event={event} />
                        ))
                      }
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <p className="text-gray-600 dark:text-gray-400">Ticket not found</p>
          </div>
        )}
      </motion.div>
    </motion.div>,
    document.body
  );
};

// Zoho-style Tab Button Component
function TabButton({
  label,
  isActive,
  onClick,
  hasDropdown = false,
}: {
  label: string;
  isActive: boolean;
  onClick: () => void;
  hasDropdown?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-3 font-medium text-xs uppercase tracking-wide border-b-2 transition-colors ${
        isActive
          ? 'border-blue-500 text-blue-600 dark:text-blue-400 bg-white dark:bg-slate-800'
          : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-white/50 dark:hover:bg-slate-700/50'
      }`}
    >
      <span>{label}</span>
      {hasDropdown && (
        <ChevronDown className="w-3 h-3" />
      )}
    </button>
  );
}

// Info Card Component
function InfoCard({
  icon,
  label,
  value,
  subtitle,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  subtitle?: string;
}) {
  return (
    <div className="bg-gray-50 dark:bg-slate-900/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 mb-1">
        {icon}
        <span className="text-xs font-medium uppercase tracking-wide">{label}</span>
      </div>
      <p className="font-semibold text-gray-900 dark:text-white text-sm">{value}</p>
      {subtitle && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{subtitle}</p>
      )}
    </div>
  );
}

// Comment Card Component
function CommentCard({ comment }: { comment: TicketComment }) {
  // Sanitize HTML content - remove script tags and dangerous attributes
  const sanitizeHtml = (html: string): string => {
    // Remove script tags and their content
    let sanitized = html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
    // Remove event handlers (onclick, onerror, etc.)
    sanitized = sanitized.replace(/\son\w+\s*=\s*["'][^"']*["']/gi, '');
    sanitized = sanitized.replace(/\son\w+\s*=\s*[^\s>]*/gi, '');
    // Remove javascript: protocol
    sanitized = sanitized.replace(/javascript:/gi, '');
    return sanitized;
  };

  const isHtmlContent = comment.content.includes('<') && comment.content.includes('>');

  return (
    <div className="bg-gray-50 dark:bg-slate-900/50 rounded-lg p-4 border-l-4 border-blue-500">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
            <User className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900 dark:text-white">
              {comment.author || 'Unknown'}
            </p>
            {comment.created_time && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {new Date(comment.created_time).toLocaleString()}
              </p>
            )}
          </div>
        </div>
        {!comment.is_public && (
          <span className="text-xs px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 rounded">
            Internal
          </span>
        )}
      </div>
      {isHtmlContent ? (
        <div
          className="text-sm text-gray-700 dark:text-gray-300 prose prose-sm dark:prose-invert max-w-none
            [&_p]:my-2 [&_br]:my-1 [&_span]:inline [&_div]:my-1
            [&_strong]:font-semibold [&_em]:italic [&_code]:bg-gray-200 [&_code]:dark:bg-gray-700 [&_code]:px-1 [&_code]:rounded"
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(comment.content) }}
        />
      ) : (
        <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
          {comment.content}
        </p>
      )}
    </div>
  );
}

// Attachment Card Component
function AttachmentCard({ attachment }: { attachment: TicketAttachment }) {
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (contentType: string) => {
    if (contentType.startsWith('image/')) return '🖼️';
    if (contentType.includes('pdf')) return '📄';
    if (contentType.includes('word') || contentType.includes('document')) return '📝';
    if (contentType.includes('sheet') || contentType.includes('excel')) return '📊';
    if (contentType.includes('zip') || contentType.includes('rar')) return '📦';
    return '📎';
  };

  return (
    <div className="bg-gray-50 dark:bg-slate-900/50 rounded-lg p-4 border-l-4 border-purple-500 hover:bg-gray-100 dark:hover:bg-slate-800/50 transition-colors">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="text-3xl">{getFileIcon(attachment.content_type)}</div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
              {attachment.file_name}
            </p>
            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mt-1">
              <span>{formatFileSize(attachment.file_size)}</span>
              <span>•</span>
              <span className="truncate">{attachment.content_type}</span>
            </div>
            {attachment.created_time && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Uploaded: {new Date(attachment.created_time).toLocaleString()}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Task Card Component
function TaskCard({ task }: { task: TicketTask }) {
  const getStatusColor = (status?: string) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'done':
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      case 'in progress':
      case 'working':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
      case 'pending':
      case 'open':
        return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400';
    }
  };

  const getPriorityColor = (priority?: string) => {
    switch (priority?.toLowerCase()) {
      case 'urgent':
        return 'border-red-500';
      case 'high':
        return 'border-orange-500';
      case 'medium':
        return 'border-yellow-500';
      case 'low':
        return 'border-green-500';
      default:
        return 'border-gray-500';
    }
  };

  return (
    <div className={`bg-gray-50 dark:bg-slate-900/50 rounded-lg p-4 border-l-4 ${getPriorityColor(task.priority)}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <CheckSquare className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
            {task.subject || 'Untitled Task'}
          </h4>
        </div>
        {task.status && (
          <span className={`text-xs px-2 py-1 rounded ${getStatusColor(task.status)}`}>
            {task.status}
          </span>
        )}
      </div>

      {task.description && (
        <p className="text-sm text-gray-700 dark:text-gray-300 mb-3 whitespace-pre-wrap">
          {task.description}
        </p>
      )}

      <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
        {task.priority && (
          <div className="flex items-center gap-1">
            <span className="font-medium">Priority:</span>
            <span>{task.priority}</span>
          </div>
        )}
        {task.assignee_name && (
          <div className="flex items-center gap-1">
            <User className="w-3 h-3" />
            <span>{task.assignee_name}</span>
          </div>
        )}
        {task.due_date && (
          <div className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            <span>Due: {new Date(task.due_date).toLocaleDateString()}</span>
          </div>
        )}
      </div>

      {task.created_time && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
          Created: {new Date(task.created_time).toLocaleString()}
        </p>
      )}
    </div>
  );
}

// History Event Card Component
function HistoryEventCard({ event }: { event: TicketHistory }) {
  const formatEventTime = (time?: string) => {
    if (!time) return '';
    const date = new Date(time);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getActionColor = (action?: string) => {
    switch (action?.toLowerCase()) {
      case 'created':
        return 'text-green-600 dark:text-green-400';
      case 'updated':
      case 'modified':
        return 'text-blue-600 dark:text-blue-400';
      case 'deleted':
      case 'removed':
        return 'text-red-600 dark:text-red-400';
      case 'commented':
        return 'text-purple-600 dark:text-purple-400';
      case 'assigned':
        return 'text-orange-600 dark:text-orange-400';
      case 'closed':
      case 'resolved':
        return 'text-gray-600 dark:text-gray-400';
      default:
        return 'text-gray-700 dark:text-gray-300';
    }
  };

  return (
    <div className="flex gap-3 pb-3 border-b border-gray-200 dark:border-gray-700 last:border-0 last:pb-0">
      <div className="flex-shrink-0 mt-1">
        <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
          <Clock className="w-4 h-4 text-blue-600 dark:text-blue-400" />
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2 mb-1">
          <div className="flex-1">
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              {event.actor || 'System'}
            </span>
            {event.action && (
              <span className={`ml-2 text-sm font-semibold ${getActionColor(event.action)}`}>
                {event.action}
              </span>
            )}
          </div>
          {event.event_time && (
            <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
              {formatEventTime(event.event_time)}
            </span>
          )}
        </div>
        {event.field_name && (
          <div className="text-sm text-gray-600 dark:text-gray-400">
            <span className="font-medium">{event.field_name}:</span>
            {event.previous_value && event.current_value ? (
              <span className="ml-1">
                changed from <span className="font-medium text-gray-700 dark:text-gray-300">"{event.previous_value}"</span>
                {' '} to <span className="font-medium text-gray-700 dark:text-gray-300">"{event.current_value}"</span>
              </span>
            ) : event.current_value ? (
              <span className="ml-1">
                set to <span className="font-medium text-gray-700 dark:text-gray-300">"{event.current_value}"</span>
              </span>
            ) : event.previous_value ? (
              <span className="ml-1">
                removed value <span className="font-medium text-gray-700 dark:text-gray-300">"{event.previous_value}"</span>
              </span>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

// Time Entry Card Component
function TimeEntryCard({ entry }: { entry: TicketTimeEntry }) {
  const formatDuration = (hours: number, minutes: number): string => {
    if (hours === 0 && minutes === 0) return '0h';
    const parts: string[] = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    return parts.join(' ');
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getBillStatusColor = (status?: string) => {
    switch (status?.toLowerCase()) {
      case 'billed':
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      case 'unbilled':
        return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'non-billable':
        return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400';
      default:
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
    }
  };

  const totalCost = entry.cost_per_hour && (entry.hours_worked + entry.minutes_worked / 60) > 0
    ? entry.cost_per_hour * (entry.hours_worked + entry.minutes_worked / 60)
    : null;

  return (
    <div className="bg-gray-50 dark:bg-slate-900/50 rounded-lg p-4 border-l-4 border-blue-500">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3 flex-1">
          <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
            <Timer className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900 dark:text-white">
              {entry.owner_name || 'Unknown'}
            </p>
            {entry.execution_date && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {formatDate(entry.execution_date)}
              </p>
            )}
          </div>
        </div>
        <div className="text-right">
          <p className="text-lg font-bold text-blue-600 dark:text-blue-400">
            {formatDuration(entry.hours_worked, entry.minutes_worked)}
          </p>
          {entry.bill_status && (
            <span className={`inline-block mt-1 text-xs px-2 py-1 rounded ${getBillStatusColor(entry.bill_status)}`}>
              {entry.bill_status}
            </span>
          )}
        </div>
      </div>

      {entry.description && (
        <p className="text-sm text-gray-700 dark:text-gray-300 mb-2 whitespace-pre-wrap">
          {entry.description}
        </p>
      )}

      <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400 mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
        {entry.cost_per_hour && (
          <div className="flex items-center gap-1">
            <span className="font-medium">Rate:</span>
            <span>${entry.cost_per_hour.toFixed(2)}/hr</span>
          </div>
        )}
        {totalCost && (
          <div className="flex items-center gap-1">
            <span className="font-medium">Total:</span>
            <span className="text-green-600 dark:text-green-400 font-semibold">
              ${totalCost.toFixed(2)}
            </span>
          </div>
        )}
        {entry.created_time && (
          <div className="flex items-center gap-1 ml-auto">
            <Clock className="w-3 h-3" />
            <span>Created: {new Date(entry.created_time).toLocaleDateString()}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// Contact/Assignee Details Skeleton Loader
function ContactDetailsSkeleton() {
  return (
    <div>
      <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-2"></div>
      <div className="bg-gray-50 dark:bg-slate-900/50 rounded-xl p-4 space-y-3">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="flex items-start gap-2">
            <div className="h-3 w-20 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
            <div className="h-3 flex-1 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default TicketDetailModal;
